import React, { memo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import moment from "utils/moment/momentLocale";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Form, Radio, Button } from "antd";

// components
import CustomSelect from "components/Forms/Fields/CustomSelect";
import CustomDate from "components/Forms/Fields/CustomDate";
import CustomTime from "components/Forms/Fields/CustomTime";

// utils
import { RECURRING_OPTIONS } from "utils/helpers/helper.constant";
import { clean } from "utils/helpers/helper.general";
// helper for forms
import {
  getRecurringDate,
  setTimeRangeAfterChanges,
  setDateRangeAfterChangesForRepeaters,
  setDateRangeAfterChangesForMore,
} from "utils/forms/forms.utils";

function DurationForm({
  formData,
  type,
  selectedStudent,
  role,
  isEdit,
  isNew,
  // actions
  nextStep,
  prevStep,
  goToStep,
  result,
  // default
  className,
}) {
  const childClassName = classNames("form-absence", className);
  const { t, i18n } = useTranslation();

  const timeFormat = "HH:mm";
  const dateFormat = "DD.MM.YYYY";

  const now = (!!formData?.date_from && moment(formData?.date_from)) || moment();
  const nowTime = (!!formData?.time_from && moment(formData?.time_from)) || moment();
  const nowTimeTo = (!!formData?.time_to && moment(formData?.time_to)) || moment();

  const initFormData = {
    duration: "day",
    date_from: now,
    time_from: nowTime,
    // for duration more than 1 day
    date_to: null,
    time_to: (!!formData?.time_to && nowTimeTo) || null,
    // For  "Absence récurrente" part
    recurring: null,
    recurring_date: null,
  };

  const [isDisabled, setIsDisabled] = useState(false);
  const [durationType, setDurationType] = useState("day");
  const [recurringType, setRecurringType] = useState("every_week");
  const [isRepeate, setIsRepeate] = useState(false);

  const [currentDay, setCurrentDay] = useState(now);
  const [currentTime, setCurrentTime] = useState(nowTime);

  const [hasRecur, setHasRecur] = useState(false);

  const [isChildAbs, setIsChildAbs] = useState(
    !isNew && (!!isEdit?.origin || !!formData?.origin)
  );

  const [AbsFormDuration] = Form.useForm();

  const recurringOption = useCallback(() => RECURRING_OPTIONS(), [i18n?.language]);
  const options = [
    {
      key: "day",
      value: "day",
      label: (
        <>
          <span className="label__title">{t("absences.durationDayLabel")}</span>
        </>
      ),
    },
    {
      key: "more",
      value: "more",
      label: (
        <>
          <span className="label__title">{t("absences.durationMoreLabel")}</span>
        </>
      ),
    },
  ];



  // Clearing the state after unmounting a component
  useEffect(() => {
    // Anything in here is fired on component unmount.
    return () => {
      // Clearing the state for SelectFieldAxios component
    };
  }, []);

  /*
   * Check current form data from parent
   * Set old data to the form
   */
  useEffect(() => {
    if (formData) {
      const hasValFormData = clean(formData);
      setIsDisabled(false);
      AbsFormDuration.setFieldsValue({
        duration: hasValFormData?.duration || initFormData?.duration,
        date_from:
          (!!hasValFormData?.date_from && moment(hasValFormData?.date_from)) ||
          moment(now),
        time_from:
          (!!hasValFormData?.time_from && moment(hasValFormData?.time_from)) ||
          nowTime,
        // for duration more than 1 day
        date_to:
          (!!hasValFormData?.date_to && moment(hasValFormData?.date_to)) ||
          null,
        time_to:
          (!!hasValFormData?.time_to && moment(hasValFormData?.time_to)) ||
          nowTimeTo,
        // For  "Absence récurrente" part
        recurring: hasValFormData?.recurring || null,
        recurring_date:
          (!!hasValFormData?.recurring_date &&
            moment(hasValFormData?.recurring_date)) ||
          null,
      });

      if (hasValFormData?.recurring) {
        setRecurringType(hasValFormData.recurring);
        setIsRepeate(true);
        setHasRecur(true);
      }
      if (hasValFormData?.duration) {
        setDurationType(hasValFormData?.duration);
      }

      if (hasValFormData?.date_from) {
        setCurrentDay(moment(hasValFormData.date_from));
      } else {
        setCurrentDay(now);
      }

      if (hasValFormData?.time_from) {
        setCurrentTime(moment(hasValFormData.time_from));
      } else {
        setCurrentTime(nowTime);
      }

      if (!isNew && !!formData?.origin) {
        setIsChildAbs(!!formData?.origin);
      }

    } else {
      setCurrentDay(now);
      setCurrentTime(nowTime);
      etIsChildAbs(false);
    }

  }, [formData]);

  useEffect(() => {
    if (currentDay && isRepeate && !hasRecur) {
      const newDate = getRecurringDate(recurringType, currentDay);
      AbsFormDuration.setFieldsValue({
        recurring_date: newDate,
      });

      setDateRangeAfterChangesForRepeaters(AbsFormDuration, AbsFormDuration.getFieldsValue());
    }
  }, [isRepeate, hasRecur, currentDay]);

  const resetInitFileds = (durationVal, allval) => {
    let initFileds = {};
    const isSickness =  allval?.type == "sickness" || formData?.type == "sickness";
    const isUnjustified =  allval?.type == "unjustified" || formData?.type == "unjustified";
    const isMedical = allval?.type == "medical" || formData?.type == "medical";
    if (!!isSickness) {
      initFileds = {
        date_from:
          (!!allval?.date_from && moment(allval?.date_from)) ||
          moment(now),
        // for duration more than 1 day
        time_from:
          (!!allval?.time_from && moment(allval?.time_from)) || nowTime,
        date_to: null,
        time_to: null,
      };
    } else if (!!isUnjustified) {
      initFileds = {
        date_from:
          (!!allval?.date_from && moment(allval?.date_from)) ||
          moment(now),
        // for duration more than 1 day
        time_from:
          (!!allval?.time_from && moment(allval?.time_from)) || nowTime,
        date_to:
          (!!allval?.date_to && moment(allval?.date_to)) ||
          null,
        time_to:
          (!!allval?.time_to && moment(allval?.time_to)) ||
          null,
      };
    } else {
      switch (durationVal) {
        case "day": {
          initFileds = {
            date_from:
              (!!allval?.date_from &&
                moment(allval?.date_from)) ||
              moment(now),
            time_from:
              (!!allval?.time_from &&
                moment(allval?.time_from)) || nowTime,
            // for duration more than 1 day
            date_to: null,
            time_to:
              (!!allval?.time_to &&
                moment(allval?.time_to)) || nowTimeTo,
          };
          break;
        }
        case "more": {
          initFileds = {
            // for duration more than 1 day
            date_from:
              (!!allval?.date_from &&
                moment(allval?.date_from)) ||
              moment(now),
            time_from:
              (!!allval?.time_from &&
                moment(allval?.time_from)) || nowTime,
            date_to: null,
            time_to:
              (!!allval?.time_to &&
                moment(allval?.time_to)) || nowTimeTo,
          };
          break;
        }
      }
    }

    AbsFormDuration.setFieldsValue({
      // For  "Absence récurrente" part
      recurring: null,
      recurring_date: null,
      ...initFileds,
    });
    setHasRecur(false);
  };

  const onFinish = values => {
    let emptyKey = [];
    for (let initKey in initFormData) {
      if (!values.hasOwnProperty(initKey)) {
        emptyKey.push({
          [initKey]: null,
        });
      }
    }
    const mapped = Object.assign({}, ...emptyKey);

    result({
      ...values,
      ...mapped,
    });

    !!nextStep && nextStep();
    AbsFormDuration.resetFields();
  };

  const absFromDurationValuesChange = useCallback(
    (values, allValues) => {
      setIsDisabled(false);
      if (values?.duration) {
        setDurationType(values?.duration);
        setIsRepeate(false);
        resetInitFileds(values?.duration, allValues);
      }

      setTimeRangeAfterChanges(AbsFormDuration, allValues);
      setDateRangeAfterChangesForMore(AbsFormDuration, allValues);

      if (!!isRepeate) {
        setDateRangeAfterChangesForRepeaters(AbsFormDuration, allValues);
      }

    },
    [AbsFormDuration, isRepeate, hasRecur, currentDay, recurringType],
  );

  return (
    <div className={childClassName}>
      <Form
        form={AbsFormDuration}
        className="form-absence__form"
        name={`form_absence_${type}_duration`}
        initialValues={initFormData}
        onFinish={onFinish}
        onValuesChange={absFromDurationValuesChange}
      >
        <Form.Item name="duration" label={t("absences.duration")}>
          <Radio.Group
            className="radio-group radio-group--horizontal"
            options={options}
            optionType="Radio"
            disabled={!!isChildAbs}
          />
        </Form.Item>

        {durationType == "more" ? (
          <>
            <div className="form-row">
              <h5 className="form-row__label">{t("common.from")}</h5>
              <div className="form-row__fileds">
                <CustomDate
                  name="date_from"
                  value={currentDay}
                  onChange={val => {
                    setCurrentDay(val);
                    setHasRecur(false);
                  }}
                />
                <CustomTime
                  name="time_from"
                  label="inputs.startTime"
                  value={currentTime}
                  onChange={val => {
                    setCurrentTime(val);
                  }}
                />
              </div>
            </div>
            <div className="form-row">
              <h5 className="form-row__label">{t("common.to")}</h5>
              <div className="form-row__fileds">
                <CustomDate name="date_to" />
                <CustomTime name="time_to" label="inputs.endTime" />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="form-row">
              <h5 className="form-row__label">{t("common.from")}</h5>
              <div className="form-row__fileds full">
                <CustomDate
                  name="date_from"
                  value={currentDay}
                  onChange={val => {
                    setCurrentDay(val);
                  }}
                />
              </div>
              <div className="form-row__fileds float">
                <CustomTime
                  name="time_from"
                  label="inputs.startTime"
                  value={currentTime}
                  onChange={val => {
                    setCurrentTime(val);
                  }}
                />
                <CustomTime 
                  name="time_to"
                  label="inputs.endTime"
                />
              </div>
            </div>
          </>
        )}

        {!!!isChildAbs && !isRepeate && durationType == "day" && (
          <div
            className="is-repeate-action"
            onClick={() => {
              setIsRepeate(true);
              const newDate = getRecurringDate(recurringType, currentDay);
              AbsFormDuration.setFieldsValue({
                recurring: recurringType,
                recurring_date: newDate,
              });
              setDateRangeAfterChangesForRepeaters(AbsFormDuration, AbsFormDuration.getFieldsValue());
              setHasRecur(false);
            }}
          >
            {t("absences.recurrentAbsenceQuestion")}
          </div>
        )}

        {isRepeate && (
          <div className="is-repeate-section">
            <div className="is-repeate-header">
              <h4 className="title">{t("absences.recurrentAbsence")}</h4>
              <div
                className="is-repeate-hide"
                onClick={() => {
                  setIsRepeate(false);
                  AbsFormDuration.setFieldsValue({
                    recurring: null,
                    recurring_date: null,
                  });
                  setDateRangeAfterChangesForRepeaters(AbsFormDuration, AbsFormDuration.getFieldsValue());
                  setHasRecur(false);
                }}
              >
                {t("absences.hideRecurent")}
              </div>
            </div>
            <CustomSelect
              label={t("absences.reccurence")}
              placeholder={t("inputs.select")}
              name={`recurring`}
              options={recurringOption()}
              onSelect={(LabeledValue, option) => {
                setRecurringType(LabeledValue);
                const newDateSet = getRecurringDate(LabeledValue, currentDay);
                AbsFormDuration.setFieldsValue({
                  recurring: LabeledValue,
                  recurring_date: newDateSet,
                });
                setDateRangeAfterChangesForRepeaters(AbsFormDuration, AbsFormDuration.getFieldsValue());
                setHasRecur(false);
              }}
            />
            <CustomDate
              label={t("inputs.endDate")}
              name="recurring_date"
              //disabledDate={disabledDateFrom}
            />
          </div>
        )}

        <div className="absence-actions-wrapper">
          <Button
            className="btn-previous"
            onClick={() => {
              if (formData?.type == "medical" && role == "parent") {
                !!goToStep && goToStep(1);
              } else {
                !!prevStep && prevStep();
              }
            }}
          >
            {t("common.previous")}
          </Button>
          <Button
            className="next-btn"
            form={`form_absence_${type}_duration`}
            htmlType="submit"
            disabled={isDisabled}
          >
            {t("common.next")}
          </Button>
        </div>
      </Form>
    </div>
  );
}

DurationForm.defaultProps = {
  type: "medical",
};

DurationForm.propTypes = {
  type: PropTypes.oneOf(["medical", "no-medical", "sickness", "unjustified"]).isRequired,
  result: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
};

export default memo(DurationForm);
